import React, { Suspense, lazy, useEffect } from 'react';
import { useAuth } from '@binaa-almajal/ui-kit/hooks/useAuth';
import Spinner from '@binaa-almajal/ui-kit/components/atoms/Spinner';
import { useI18n } from '@binaa-almajal/ui-kit/hooks/useI18n';
import { attemptSilentSignIn, canAttemptSilentSignIn } from './tokenSource';

// Lazy, like every other screen: an authenticated visitor never renders this
// and should not download it.
const SignIn = lazy(() => import('../screens/SignIn'));

// Route and section guards.
//
// THESE ARE NOT SECURITY. The server refuses the call regardless of what is
// rendered, and a screen that only hides a button has protected nothing. Their
// job is to stop showing people doors that will not open — the 403 is still the
// authoritative answer and every mutation path has to handle it.

/**
 * Requires a signed-in actor.
 *
 * NOT SIGNED IN HERE IS NOT THE SAME AS NOT SIGNED IN. Someone arriving from
 * another platform application, or opening a new tab, may hold a live Keycloak
 * session and simply no Academy session yet. Showing them the gate asks for a
 * click that would complete without a prompt — so before the gate, one silent
 * prompt=none round trip (once per tab per two minutes) is tried, and the
 * spinner stays up while it runs. Only a person with no realm session at all
 * ever sees the gate.
 *
 * It lives HERE and not in the token source because only protected routes
 * should trigger it: the landing page, the catalogue, the checkout and the
 * verifier are not wrapped in RequireAuth (see AppRoutes and
 * config/publicRoutes.js), and a public page must never bounce an anonymous
 * visitor through the identity provider.
 */
export const RequireAuth = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const trySilent = !loading && !isAuthenticated && canAttemptSilentSignIn();

    useEffect(() => {
        if (trySilent) attemptSilentSignIn();
    }, [trySilent]);

    if (loading || trySilent) return <Spinner size="lg" color="primary" center />;

    if (!isAuthenticated) {
        // Deliberately not a redirect to /login: there is no login route.
        // Sign-in is a top-level navigation through the server's
        // /api/bff/login to the identity provider, which is the token
        // source's job.
        //
        // The screen renders IN PLACE rather than navigating, so the URL the
        // visitor asked for survives sign-in and they land where they meant to
        // go instead of on the cockpit.
        return (
            <Suspense fallback={<Spinner size="lg" color="primary" center />}>
                <SignIn />
            </Suspense>
        );
    }

    return children;
};

/**
 * Requires a screen permission: `<RequireScreen app="academy" screen="cohorts">`
 * (operation defaults to `view`). `screen` may also be a list of screens, any
 * of which admits -- for a route that serves more than one.
 * @param {{ app?: string, screen: string|string[], op?: 'view'|'add'|'edit'|'delete', children: React.ReactNode, fallback?: React.ReactNode }} props
 */
export const RequireScreen = ({ app = 'academy', screen, op = 'view', children, fallback = null }) => {
    const { canScreen, loading } = useAuth();
    const { t } = useI18n();

    if (loading) return <Spinner size="lg" color="primary" center />;

    const screens = Array.isArray(screen) ? screen : [screen];
    // Any-of, not all-of: a route that serves more than one screen has to
    // accept a grant on either -- requiring both would lock out everyone who
    // legitimately holds one.
    if (screens.some((name) => canScreen(app, name, op))) return children;

    return fallback ?? (
        <div className="p-6 text-center text-gray-600 dark:text-gray-400">
            {t('error.forbidden')}
        </div>
    );
};

/** Inline variant: renders nothing rather than a message. For buttons. */
export const IfScreen = ({ app = 'academy', screen, op = 'view', children }) => {
    const { canScreen } = useAuth();
    const screens = Array.isArray(screen) ? screen : [screen];
    return screens.some((name) => canScreen(app, name, op)) ? children : null;
};
